import { useState } from 'react';
import { api, ApiError } from '@/lib/api';
import { Button } from '@/components/ui';
import { SupportPill } from '@/components/SupportPill';
import { t } from '@/lib/i18n';

/**
 * Landing page for the emailed reset link (/auth/reset?token=…).
 *
 * Completing a reset deliberately does NOT sign the user in — it sends them to
 * the login page instead. Minting a session straight from a link in an inbox
 * would make the reset mail a login bypass, which is exactly what the server
 * refuses to do.
 */
export function ResetPasswordPage() {
  const token = new URLSearchParams(window.location.search).get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (password !== confirm) {
      setError(t('resetPasswordPage.passwordsDoNotMatch'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.resetPassword(token, password);
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('resetPasswordPage.connectionFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-white px-4">
      <div className="w-full max-w-[400px]">
        <div className="rounded-2xl bg-white p-10 text-gray-900 shadow-[0_8px_32px_rgba(0,0,0,0.18)]">
          <div className="mb-6 flex flex-col items-center gap-1">
            <img src="/aicotone-logo.png" alt={t('resetPasswordPage.logoAlt')} className="h-12 w-auto" />
            <span className="text-xs font-medium uppercase tracking-widest text-gray-400">{t('resetPasswordPage.productTagline')}</span>
          </div>

          {!token ? (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-[13px] text-red-700" role="alert">
              {t('resetPasswordPage.incompleteLink')}
            </p>
          ) : done ? (
            <div className="space-y-4">
              <p className="rounded-lg bg-green-50 px-3 py-2 text-[13px] text-green-800" role="status">
                {t('resetPasswordPage.passwordChanged')}
              </p>
              <Button variant="primary" className="w-full" onClick={() => window.location.assign('/login')}>
                {t('resetPasswordPage.goToSignIn')}
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <p className="text-[13px] text-gray-600">{t('resetPasswordPage.chooseNewPassword')}</p>
              <label className="block">
                <span className="mb-1 block text-[13px] font-medium text-gray-700">{t('resetPasswordPage.newPassword')}</span>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoFocus
                  autoComplete="new-password" minLength={12}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500" />
              </label>
              <label className="block">
                <span className="mb-1 block text-[13px] font-medium text-gray-700">{t('resetPasswordPage.confirmNewPassword')}</span>
                <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required
                  autoComplete="new-password" minLength={12}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500" />
              </label>
              <p className="text-[12px] text-gray-500">{t('resetPasswordPage.atLeastTwelveChars')}</p>
              {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-[13px] text-red-700" role="alert">{error}</p>}
              <Button type="submit" variant="primary" loading={busy} className="w-full">{t('resetPasswordPage.setNewPassword')}</Button>
            </form>
          )}
        </div>
      </div>
      <SupportPill signedOut />
    </div>
  );
}
